import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest } from "rxjs";
import { first } from "rxjs/operators";
import { AbstractAuthorizationService } from "src/app/authorization/services/abstractAuthorization.service";
import { HelpTarget } from "src/app/interfaces/helpTarget";
import { LogClass } from "src/app/logger";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { TemplateFacade } from "src/app/services/template.facade";
import { selectCurrentUser } from "src/app/user/state";
import { SubSink } from "subsink";
import {
  ICirculationTemplate,
  ICirculationTemplateStep,
  IMetaData,
  IStepInit
} from "../../interfaces";
import { Logger } from "../../logger/logger";
import { NavigationService } from "../../services/navigation.service";

@Component({
    selector: "circ-details-template",
    templateUrl: "./details.component.html",
    styleUrls: ["./details.component.scss"],
    standalone: false
})
@LogClass({ source: "Templates/DetailsComponent" })
export class DetailsComponent implements OnInit, OnDestroy {
  public canDelete = false;

  public template: ICirculationTemplate = null;
  public steps: ICirculationTemplateStep[] = [];
  public isReady = true;
  public readonly helpTargetMetadata = HelpTarget.Metadata;
  public readonly helpTargetSteps = HelpTarget.Steps;

  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private templateFacade: TemplateFacade,
    private circulationFacade: CirculationFacade,
    private navigate: NavigationService,
    private store: Store,
    private authorizationService: AbstractAuthorizationService
  ) {
    this.logger.debug("ctor");
  }

  public addStep(): void {
    this.navigate.toTemplateStepCreation(this.template.id);
  }

  public addMetaData(): void {
    this.navigate.toMetaDataCreation(this.template.id);
  }

  public cellClickHandler({ dataItem }: { dataItem: IStepInit }): void {
    this.navigate.toStep(dataItem.circulationId, dataItem.id);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.logger.info("init");

    this.subscriptions.sink = combineLatest([
      this.store.select(selectCurrentUser),
      this.templateFacade.getCurrentTemplate()
    ]).subscribe(
      ([user, t]) => {
        this.logger.info("loaded template:" + t.id);
        const isOwner = user.id == t.permissions?.ownerId;

        this.template = t;
        this.isReady = !!t;
        this.steps = t?.steps;
        this.canDelete =
          isOwner || this.authorizationService.isAdmin(user.roles);

        this.circulationFacade.getCirculationProps();
      },
      (err) => {
        this.logger.error("loading template", err);
        this.navigate.toError(err);
      }
    );
  }

  public onEdit(step: ICirculationTemplateStep): void {
    this.navigate.toTemplateStepEdit(this.template.id, step.stepNo);
  }

  public deleteData(data: IMetaData, template: ICirculationTemplate): void {
    this.templateFacade.deleteMetaData(data, template).subscribe(() => {
      this.logger.info(
        "deleted metadata: " + data.key + " - " + data.valueType
      );

      this.template.payload.data = this.template.payload.data.filter(
        (d) => d != data
      );
    });
  }

  public editData(data: IMetaData, template: ICirculationTemplate): void {
    this.navigate.toMetaDataEdit(template.id, data);
  }

  public editTemplate(template: ICirculationTemplate): void {
    this.navigate.toTemplateEdit(template.id);
  }

  public deleteStep(
    step: ICirculationTemplateStep,
    template: ICirculationTemplate
  ): void {
    this.templateFacade
      .deleteStep(step, template)
      .pipe(first())
      .subscribe(() => {
        this.logger.info("deleted template step no.: " + step.stepNo);

        this.steps = this.steps.filter((x) => x.stepNo != step.stepNo);
      });
  }

  public exit(): void {
    this.navigate.toTemplates();
  }

  public deleteTemplate(template: ICirculationTemplate): void {
    this.subscriptions.sink = this.templateFacade.delete(template).subscribe(
      () => {
        this.logger.debug("Deleting Template", template);
        this.navigate.toTemplates();
      },
      (err) => {
        this.logger.error("deleting template", err);
        this.navigate.toError(err);
      }
    );
  }
}
